/**
 * The browser's DPoP key: one ECDSA P-256 key pair per browser profile, generated NON-EXTRACTABLE.
 * Page script - ours, an extension's, or anything pasted into the console - can ask the browser to
 * sign with it, but can never read or export the private half. That is what makes a DPoP-bound token
 * worthless once it is copied out of the browser.
 *
 * WHY ITS OWN DATABASE. The key lives in `gorelo-dpop`, not in Dexie's `GoreloWeb_<technicianId>`,
 * because that database is wiped by the InvalidRegion handler and by `Dexie.delete(...)`. Losing the
 * key mid-session would orphan the bound token and force a re-exchange on every such wipe.
 *
 * WHY `add`, NOT `put`. Two tabs opened together can both find no key and both generate one. `add`
 * fails with ConstraintError for whichever writes second; that tab then re-reads and adopts the
 * winner's key, so every tab of a profile signs with the same key. With `put` the second write would
 * silently replace the first, and the first tab's token would stop matching the stored key.
 */

const DB_NAME = 'gorelo-dpop';
const STORE_NAME = 'keys';
const KEY_ID = 'device';

export interface DpopPublicJwk {
    kty: 'EC';
    crv: 'P-256';
    x: string;
    y: string;
}

export interface DpopKey {
    keyPair: CryptoKeyPair;
    /** Only the RFC 7638 required members, so it serialises into proofs exactly as the gateway hashes it. */
    publicJwk: DpopPublicJwk;
}

let loading: Promise<DpopKey> | null = null;

/** The profile's DPoP key, created on first use. Rejects if IndexedDB or WebCrypto is unavailable. */
export function getDpopKey(): Promise<DpopKey> {
    if (!loading) {
        loading = loadOrCreate().catch((error) => {
            loading = null;
            throw error;
        });
    }
    return loading;
}

/** Forget the key, so the next sign-in on this profile starts with a fresh one. */
export async function deleteDpopKey(): Promise<void> {
    loading = null;
    const db = await openDb();
    try {
        await runRequest(db, 'readwrite', (store) => store.delete(KEY_ID));
    } finally {
        db.close();
    }
}

async function loadOrCreate(): Promise<DpopKey> {
    const db = await openDb();
    try {
        let keyPair = await readKeyPair(db);
        if (!keyPair) {
            const generated = await crypto.subtle.generateKey(
                { name: 'ECDSA', namedCurve: 'P-256' },
                false, // non-extractable: applies to the private key; a public key is always exportable
                ['sign', 'verify'],
            );
            try {
                await runRequest(db, 'readwrite', (store) => store.add(generated, KEY_ID));
                keyPair = generated;
            } catch (error) {
                if ((error as DOMException | null)?.name !== 'ConstraintError') throw error;
                keyPair = await readKeyPair(db); // another tab stored its key first; adopt it
            }
        }
        if (!keyPair) throw new Error('DPoP key could not be created');
        return { keyPair, publicJwk: await exportPublicJwk(keyPair.publicKey) };
    } finally {
        db.close();
    }
}

async function exportPublicJwk(publicKey: CryptoKey): Promise<DpopPublicJwk> {
    const jwk = await crypto.subtle.exportKey('jwk', publicKey);
    if (!jwk.x || !jwk.y) throw new Error('DPoP public key export is missing coordinates');
    return { kty: 'EC', crv: 'P-256', x: jwk.x, y: jwk.y };
}

function openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = () => {
            if (!request.result.objectStoreNames.contains(STORE_NAME)) {
                request.result.createObjectStore(STORE_NAME);
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new Error('DPoP key store is blocked by another tab'));
    });
}

async function readKeyPair(db: IDBDatabase): Promise<CryptoKeyPair | undefined> {
    const value = await runRequest<unknown>(db, 'readonly', (store) => store.get(KEY_ID));
    const pair = value as Partial<CryptoKeyPair> | undefined;
    return pair?.privateKey && pair?.publicKey ? (pair as CryptoKeyPair) : undefined;
}

/** One request in its own transaction; resolves only once the transaction has committed. */
function runRequest<T>(
    db: IDBDatabase,
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest,
): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, mode);
        const request = operation(transaction.objectStore(STORE_NAME));
        let result: T;
        request.onsuccess = () => {
            result = request.result as T;
        };
        transaction.oncomplete = () => resolve(result);
        transaction.onerror = () => reject(request.error ?? transaction.error);
        transaction.onabort = () => reject(request.error ?? transaction.error);
    });
}
